import * as SelectPrimitive from '@radix-ui/react-select';
import * as React from 'react';
import { useKeyboardNav } from '../../hooks/useKeyboardNav';
import { cn } from '../../styles/lib/utils';
import { Icon } from '../Icon';

type SelectSide = 'top' | 'bottom' | null;

type SelectContextValue = {
  side: SelectSide;
  setSide: (side: SelectSide) => void;
  isClearable: boolean;
  hasValue: boolean;
  disabled: boolean;
  clear: () => void;
};

const SelectContext = React.createContext<SelectContextValue>({
  side: null,
  setSide: () => {},
  isClearable: false,
  hasValue: false,
  disabled: false,
  clear: () => {},
});

type SelectRootProps = React.ComponentProps<typeof SelectPrimitive.Root> & {
  isClearable?: boolean;
};

/**
 * Radix Select root, the escape hatch for popups the batteries-included `Select` does not cover: grouped sections,
 * separators, non-standard triggers. Reach for `Select` by default.
 */
function SelectRoot({ isClearable = false, ...props }: SelectRootProps) {
  const { value: valueProp, defaultValue, onValueChange, disabled = false } = props;
  const [side, setSide] = React.useState<SelectSide>(null);
  const [internalValue, setInternalValue] = React.useState(defaultValue ?? '');
  const isControlled = valueProp !== undefined;
  const currentValue = isControlled ? valueProp : internalValue;

  const handleValueChange = React.useCallback(
    (next: string) => {
      if (!isControlled) setInternalValue(next);
      onValueChange?.(next);
    },
    [isControlled, onValueChange],
  );

  // Radix shows the placeholder for an empty-string value, so a clearable select always runs controlled.
  const clearableProps = isClearable ? { value: currentValue, onValueChange: handleValueChange } : {};

  return (
    <SelectContext.Provider
      value={{
        side,
        setSide,
        isClearable,
        hasValue: isClearable && currentValue !== '',
        disabled,
        clear: () => handleValueChange(''),
      }}
    >
      <SelectPrimitive.Root data-slot="select" {...props} {...clearableProps} />
    </SelectContext.Provider>
  );
}

function SelectGroup({ ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return (
    <SelectPrimitive.Value data-slot="select-value" className={cn('flex items-center h-full', className)} {...props} />
  );
}

/** Shared by `Select` and `MultiSelect` so the two triggers cannot drift apart. */
const selectTriggerClassName = cn(
  'h-7 w-full min-w-0 rounded border bg-white px-2 text-xs leading-[1.4] text-grey-80 transition-[color,box-shadow,border-color] outline-none',
  'border-grey-30',
  'focus-visible:border-focus',
  'data-[state=open]:border-blue-50',
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-grey-10',
  'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
  'dark:bg-white/30 dark:aria-invalid:ring-destructive/40',
  'flex items-center justify-between gap-2',
  '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:size-4',
);

function SelectTrigger({
  className,
  children,
  ref,
  disabled,
  clearLabel,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & { clearLabel?: string }) {
  const { side, isClearable, hasValue, disabled: rootDisabled, clear } = React.useContext(SelectContext);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const composedRef = React.useCallback(
    (el: HTMLButtonElement | null) => {
      triggerRef.current = el;
      setRef(ref, el);
    },
    [ref],
  );
  // Default to 'bottom' when open so rounding applies before Content reports side
  const effectiveSide = side ?? 'bottom';
  const showClear = isClearable && hasValue && !rootDisabled && !disabled;
  const testId = (props as { 'data-testid'?: string })['data-testid'];
  // Names the clear control after the field so several clearable selects on one form stay distinguishable.
  const clearName = clearLabel ?? props['aria-label'];
  const clearControlLabel = clearName ? `Clear ${clearName}` : 'Clear selection';

  const trigger = (
    <SelectPrimitive.Trigger
      ref={composedRef}
      data-slot="select-trigger"
      disabled={disabled}
      className={cn(
        selectTriggerClassName,
        effectiveSide === 'bottom' && 'data-[state=open]:rounded-b-none',
        effectiveSide === 'top' && 'data-[state=open]:rounded-t-none data-[state=open]:rounded-b',
        'data-placeholder:text-grey-50',
        '*:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 *:data-[slot=select-value]:h-full *:data-[slot=select-value]:leading-normal',
        showClear && '*:data-[slot=select-value]:mr-5',
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <Icon icon="chevronDown" className="size-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );

  if (!isClearable) return trigger;

  // The clear control sits beside the trigger because a button nested in it is invalid and its pointerdown opens the menu.
  return (
    <div data-slot="select-trigger-wrapper" className="relative flex w-full min-w-0 items-center">
      {trigger}
      {showClear && (
        <button
          type="button"
          aria-label={clearControlLabel}
          data-testid={testId ? `${testId}-clear` : undefined}
          className="absolute right-7 flex size-4 cursor-pointer items-center justify-center rounded text-grey-80 hover:bg-grey-10 focus-ring"
          onClick={() => {
            clear();
            triggerRef.current?.focus();
          }}
        >
          <Icon icon="cross2" size={14} />
        </button>
      )}
    </div>
  );
}

function setRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (ref == null) return;
  if (typeof ref === 'function') ref(value);
  else (ref as React.RefObject<T | null>).current = value;
}

const SelectContent = React.forwardRef<HTMLDivElement, React.ComponentProps<typeof SelectPrimitive.Content>>(
  function SelectContent(
    { className, children, position = 'popper', align = 'start', sideOffset = -1, ...props },
    forwardedRef,
  ) {
    const observerRef = React.useRef<MutationObserver | null>(null);
    const { setSide } = React.useContext(SelectContext);
    const keyboardNav = useKeyboardNav();

    // Run when content element is attached (on open); Radix only mounts the content DOM when open, so effect-on-mount would see null
    const contentRefCallback = React.useCallback(
      (el: HTMLDivElement | null) => {
        setRef(forwardedRef, el);
        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
        setSide(null);

        if (el) {
          const applySide = () => {
            const value = el.getAttribute('data-side');
            if (value === 'top' || value === 'bottom') setSide(value);
          };
          applySide();
          const observer = new MutationObserver(applySide);
          observer.observe(el, { attributes: true, attributeFilter: ['data-side'] });
          observerRef.current = observer;
        }
      },
      [setSide, forwardedRef],
    );

    return (
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          ref={contentRefCallback}
          data-slot="select-content"
          data-keyboard-nav={keyboardNav || undefined}
          className={cn(
            'bg-white text-grey-80 data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95',
            'data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
            // z-[150] clears the client's legacy Dialog stack (content up to z 140) and Sheet (z 100).
            ' data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-[150]',
            'max-h-(--radix-select-content-available-height) w-(--radix-select-trigger-width)',
            'origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto border shadow-md',
            'rounded-t-none rounded-b',
            'data-[side=top]:rounded-t data-[side=top]:rounded-b-none',
            'border-grey-30 data-[state=open]:border-blue-50',
            'dark:bg-white/30',
            className,
          )}
          position={position}
          align={align}
          sideOffset={sideOffset}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            className={cn(
              'p-1',
              position === 'popper' &&
                'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width) scroll-my-1',
            )}
          >
            {children}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    );
  },
);

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn('text-grey-50 px-2 py-1.5 text-xs', className)}
      {...props}
    />
  );
}

function SelectItem({ className, children, ...props }: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        'relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-xs leading-[1.4] text-grey-80 outline-hidden select-none',
        'focus:bg-grey-10 focus:text-grey-80 hover:bg-grey-10 hover:text-grey-80',
        // Radix focuses the option under the pointer, so the ring shows only while the keyboard is in use.
        'in-data-[keyboard-nav=true]:data-highlighted:highlight-ring',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:size-4',
        '*:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2',
        className,
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Icon icon="check" className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('bg-grey-30 pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <Icon icon="chevronUp" className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <Icon icon="chevronDown" className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export type { SelectRootProps };
export {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  selectTriggerClassName,
  SelectValue,
};
